import { Box, Grid, Typography, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { MonthlyData } from "../types";
import { TOYOTA_RED, INFO_BLUE, SUCCESS_GREEN, WARNING_ORANGE } from "../theme";
import KpiCard from "../components/KpiCard";
import PlotlyChart from "../components/PlotlyChart";
import ChartCard from "../components/ChartCard";
import TableCard from "../components/TableCard";

interface Props { data: MonthlyData; compact?: boolean; }

const PlatformAdoption = ({ data, compact }: Props) => {
  const latest = data.adoptionData[data.adoptionData.length - 1];
  const totalPodsOnboarded = latest?.totalPods ?? 0;

  // Use backend pods-by-program data, fallback to pod-level grouping
  const pods = [...new Set(data.activeUsers.map((u) => u.pod))];
  const totalPrograms = data.podsByProgram?.length ?? pods.length;
  const activePods = data.totalPods || pods.length;

  // Monthly onboarded (latest month new pods)
  const monthlyOnboarded = latest?.newPods ?? 0;

  const programPodCounts = (data.podsByProgram && data.podsByProgram.length > 0)
    ? [...data.podsByProgram].sort((a, b) => b.count - a.count)
    : pods.map((pod) => ({ program: pod, count: 1 }));

  const sp = compact ? 1 : 2;
  const chartH = compact ? 260 : 320;

  return (
    <Box>
      {!compact && (
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: "#1B2559" }}>
          📈 Platform Adoption
        </Typography>
      )}

      {/* KPI Cards */}
      <Grid container spacing={sp} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={compact ? 6 : 3}>
          <KpiCard label="Total Pods Onboarded" value={totalPodsOnboarded} color={TOYOTA_RED} />
        </Grid>
        <Grid item xs={6} sm={compact ? 6 : 3}>
          <KpiCard label="Total Programs" value={totalPrograms} color={INFO_BLUE} />
        </Grid>
        <Grid item xs={6} sm={compact ? 6 : 3}>
          <KpiCard
            label={`${data.displayName?.split(" ")[0] || "Monthly"} Onboarded`}
            value={`${monthlyOnboarded} pods`}
            color={SUCCESS_GREEN}
          />
        </Grid>
        <Grid item xs={6} sm={compact ? 6 : 3}>
          <KpiCard label="Active Pods" value={activePods} color={WARNING_ORANGE} />
        </Grid>
      </Grid>

      {/* Cumulative Pod Onboarding Trend & Pods Onboarded per Month */}
      <Grid container spacing={sp} sx={{ mb: 3 }}>
        <Grid item xs={12} md={compact ? 12 : 6}>
          <ChartCard title="Cumulative Pod Onboarding Trend">
            <PlotlyChart
              data={[{
                x: data.adoptionData.map((d) => d.date),
                y: data.adoptionData.map((d) => d.totalPods),
                type: "scatter",
                mode: "lines+markers",
                name: "Pods",
                line: { color: TOYOTA_RED, width: 2 },
                marker: { size: 6 },
              }]}
              layout={{ xaxis: { title: "Date" }, yaxis: { title: "Cumulative Pods" }, hovermode: "x unified" }}
              height={chartH}
            />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={compact ? 12 : 6}>
          <ChartCard title="Pods Onboarded per Month">
            <PlotlyChart
              data={[{
                x: data.adoptionData.map((d) => d.date),
                y: data.adoptionData.map((d) => d.newPods),
                type: "bar",
                name: "Pods Onboarded",
                marker: { color: INFO_BLUE },
              }]}
              layout={{ xaxis: { title: "Date" }, yaxis: { title: "Pods Onboarded" } }}
              height={chartH}
            />
          </ChartCard>
        </Grid>
      </Grid>

      {/* Active Pods by Program */}
      <Box sx={{ mb: 3 }}>
        <ChartCard title="Pods by Program">
          <PlotlyChart
            data={[{
              x: programPodCounts.map((p) => p.program.length > 20 ? p.program.slice(0, 20) + "…" : p.program),
              y: programPodCounts.map((p) => p.count),
              type: "bar",
              marker: { color: TOYOTA_RED },
              hovertext: programPodCounts.map((p) => `${p.program}: ${p.count}`),
              hoverinfo: "text",
            }]}
            layout={{
              xaxis: { tickangle: -45, tickfont: { size: compact ? 8 : 9 } },
              yaxis: { title: "Count", dtick: 1, range: [0, Math.max(...programPodCounts.map((p) => p.count)) + 0.5] },
              margin: { t: 10, r: 20, b: 150, l: 50 },
            }}
            height={compact ? 320 : 420}
          />
        </ChartCard>
      </Box>

      {/* Program-wise Metrics Table */}
      <TableCard title="Program-wise Metrics" titleColor={TOYOTA_RED} paperSx={{ maxHeight: 400, overflowY: "auto" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ bgcolor: "#f5f5f5" }}>
              <TableCell sx={{ fontWeight: 600 }}>Program</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Pod Count</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {programPodCounts.map((row, i) => (
              <TableRow key={i}>
                <TableCell>{row.program}</TableCell>
                <TableCell align="right">{row.count}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableCard>
    </Box>
  );
};

export default PlatformAdoption;